import React from 'react'
import { Button } from '../../../../ui/Button'
import { Spinner } from '../../../../ui/Spinner'
import { InfoAlert } from './InfoAlert'
import type { AppointmentState, UserInfo } from '../types'

interface AppointmentConfirmationProps {
  appointmentData: AppointmentState | null
  isPollingData: boolean
  userInfo: UserInfo
  onContinue?: () => void
}

export const AppointmentConfirmation: React.FC<
  AppointmentConfirmationProps
> = ({ appointmentData, isPollingData, userInfo, onContinue }) => {
  console.log(appointmentData?.complete)
  return (
    <div className="max-w-2xl mx-auto text-center">
      <div className="mb-8">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg
            className="w-10 h-10 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          ¡Cita agendada exitosamente!
        </h2>

        <p className="text-gray-600 mb-6">
          Tu cita de verificación ha sido programada.
          {isPollingData ? ' Obteniendo detalles completos...' : ''}
        </p>
      </div>

      {/* Appointment Details */}
      <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">
          Detalles de la cita:
        </h3>

        {isPollingData ? (
          <div className="flex items-center justify-center py-4">
            <Spinner size="sm" />
            <span className="ml-2 text-gray-600">
              Obteniendo información completa...
            </span>
          </div>
        ) : (
          <div className="space-y-3">
            {appointmentData?.complete ? (
              // Complete data from backend
              <>
                <div className="flex items-start">
                  <span className="text-gray-500 mr-2">👤</span>
                  <div className="flex w-full">
                    <span className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">
                      Contacto:
                    </span>
                    <span className="text-sm text-gray-900">
                      {appointmentData.complete.userName}
                    </span>
                  </div>
                </div>

                <div className="flex items-start">
                  <span className="text-gray-500 mr-2">📧</span>
                  <div className="flex w-full">
                    <span className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">
                      Email:
                    </span>
                    <span className="text-sm text-gray-900">
                      {appointmentData.complete.userEmail}
                    </span>
                  </div>
                </div>

                <div className="flex items-start">
                  <span className="text-gray-500 mr-2">📅</span>
                  <div className="flex w-full">
                    <span className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">
                      Fecha:
                    </span>
                    <span className="text-sm text-gray-900">
                      {appointmentData.complete.startTime
                        ? new Date(
                            appointmentData.complete.startTime
                          ).toLocaleString('es-MX')
                        : 'Por confirmar'}
                    </span>
                  </div>
                </div>

                <div className="flex items-start">
                  <span className="text-gray-500 mr-2">📍</span>
                  <div className="flex w-full">
                    <span className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">
                      Dirección:
                    </span>
                    <span className="text-sm text-gray-900 break-words">
                      {appointmentData.complete.meetingLocation ||
                        userInfo.primaryAddress ||
                        'Por definir'}
                    </span>
                  </div>
                </div>

                {appointmentData.complete.timezone && (
                  <div className="flex items-start">
                    <span className="text-gray-500 mr-2">🌍</span>
                    <div className="flex w-full">
                      <span className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">
                        Zona horaria:
                      </span>
                      <span className="text-sm text-gray-900">
                        {appointmentData.complete.timezone}
                      </span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              // Basic data from Calendly
              <>
                <div className="flex items-start">
                  <span className="text-gray-500 mr-2">👤</span>
                  <div className="flex w-full">
                    <span className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">
                      Contacto:
                    </span>
                    <span className="text-sm text-gray-900">
                      {userInfo.name || 'No especificado'}
                    </span>
                  </div>
                </div>

                <div className="flex items-start">
                  <span className="text-gray-500 mr-2">📧</span>
                  <div className="flex w-full">
                    <span className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">
                      Email:
                    </span>
                    <span className="text-sm text-gray-900">
                      {userInfo.email}
                    </span>
                  </div>
                </div>

                <div className="flex items-start">
                  <span className="text-gray-500 mr-2">⏰</span>
                  <div className="flex w-full">
                    <span className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">
                      Programada:
                    </span>
                    <span className="text-sm text-gray-900">
                      {appointmentData?.scheduledAt
                        ? new Date(appointmentData.scheduledAt).toLocaleString(
                            'es-MX'
                          )
                        : 'Ahora'}
                    </span>
                  </div>
                </div>

                {userInfo.primaryAddress && (
                  <div className="flex items-start">
                    <span className="text-gray-500 mr-2">📍</span>
                    <div className="flex w-full">
                      <span className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">
                        Dirección:
                      </span>
                      <span className="text-sm text-gray-900 break-words">
                        {userInfo.primaryAddress}
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <p className="text-xs text-gray-500 mt-4">
          Recibirás un correo de confirmación con todos los detalles de tu cita.
        </p>
      </div>

      <InfoAlert
        title="¿Qué sigue?"
        description="Recibirás un correo de confirmación con los detalles de tu cita. Automáticamente procederemos al siguiente paso del proceso de onboarding."
      />

      {/* Continue button */}
      {!isPollingData && onContinue && (
        <Button
          onClick={onContinue}
          variant="primary"
          className="px-12 py-3 min-w-[230px] mt-6"
        >
          Continuar
        </Button>
      )}
    </div>
  )
}
